import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  searchOff,
} from '@/lib/api/products';
import type {
  SearchProductsQuery,
  CreateProductRequest,
  UpdateProductRequest,
  SearchOffQuery,
} from '@/types/api';
import { productKeys } from '@/lib/query-keys';

export function useProducts(params?: SearchProductsQuery) {
  return useQuery({
    queryKey: productKeys.list(params),
    queryFn: () => getProducts(params),
    staleTime: 1000 * 60 * 5,
  });
}

export function useSearchOff(params: SearchOffQuery | null) {
  return useQuery({
    queryKey: params ? productKeys.offSearch(params) : ['off-search', 'disabled'],
    queryFn: () => searchOff(params!),
    enabled: !!params?.query,
    staleTime: 1000 * 60 * 10,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductRequest) => createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProductRequest }) =>
      updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
